import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export const WindDirectionEnum = {
    N: "N",
    NE: "NE",
    E: "E",
    SE: "SE",
    S: "S",
    SW: "SW",
    W: "W",
    NW: "NW",
} as const;
export type WindDirectionEnum =
    (typeof WindDirectionEnum)[keyof typeof WindDirectionEnum];
const WindDirectionEnumSchema = z.enum(WindDirectionEnum);

const WeatherDaySchema = z
    .object({
        date: z.string().trim(),
        etc: z.number().nullable(),
        eto: z.number().nullable(),
        kc: z.number().nullable(),
        humidity: z.number(),
        windSpeedFrom: z.number(),
        windSpeedTo: z.number(),
        windDirection: WindDirectionEnumSchema,
        sunset: z.string().trim(),
        sunrise: z.string().trim(),
        icon: z.string().trim(),
        rainProb: z.number(),
        rainFall: z.number(),
        minTemp: z.number(),
        maxTemp: z.number(),
    })
    .brand<"WeatherDay">();

export type WeatherDayInputProps = z.input<typeof WeatherDaySchema>;
type WeatherDayProps = z.output<typeof WeatherDaySchema>;

export class WeatherDay {
    private constructor(private readonly props: Readonly<WeatherDayProps>) {}

    get date(): string {
        return this.props.date;
    }

    get etc(): Nullable<number> {
        return this.props.etc;
    }

    get eto(): Nullable<number> {
        return this.props.eto;
    }

    // eslint-disable-next-line id-length
    get kc(): Nullable<number> {
        return this.props.kc;
    }

    get humidity(): number {
        return this.props.humidity;
    }

    get windSpeedFrom(): number {
        return this.props.windSpeedFrom;
    }

    get windSpeedTo(): number {
        return this.props.windSpeedTo;
    }

    get windDirection(): WindDirectionEnum {
        return this.props.windDirection;
    }

    get sunset(): string {
        return this.props.sunset;
    }

    get sunrise(): string {
        return this.props.sunrise;
    }

    get icon(): string {
        return this.props.icon;
    }

    get rainProb(): number {
        return this.props.rainProb;
    }

    get rainFall(): number {
        return this.props.rainFall;
    }

    get minTemp(): number {
        return this.props.minTemp;
    }

    get maxTemp(): number {
        return this.props.maxTemp;
    }

    static validate(
        props: Readonly<MaybeValidProps<WeatherDayInputProps>>,
    ): Result<WeatherDayProps, z.ZodError<WeatherDayProps>> {
        const result = WeatherDaySchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<WeatherDayInputProps>,
    ): Result<WeatherDay, z.ZodError<WeatherDayProps>> {
        return WeatherDay.validate(props).map(
            (validatedProps) => new WeatherDay(validatedProps),
        );
    }

    toCreateProps(): WeatherDayInputProps {
        return {
            date: this.props.date,
            etc: this.props.etc,
            eto: this.props.eto,
            kc: this.props.kc,
            humidity: this.props.humidity,
            windSpeedFrom: this.props.windSpeedFrom,
            windSpeedTo: this.props.windSpeedTo,
            windDirection: this.props.windDirection,
            sunset: this.props.sunset,
            sunrise: this.props.sunrise,
            icon: this.props.icon,
            rainProb: this.props.rainProb,
            rainFall: this.props.rainFall,
            minTemp: this.props.minTemp,
            maxTemp: this.props.maxTemp,
        };
    }
}

const WeatherDaysSchema = z
    .object({
        weatherDays: z.array(
            z.custom<WeatherDay>((val) => val instanceof WeatherDay, {
                error: "Invalid WeatherDay instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"WeatherDays">();

export type WeatherDaysInputProps = z.input<typeof WeatherDaysSchema>;
export type WeatherDaysProps = z.output<typeof WeatherDaysSchema>;

export class WeatherDays {
    private constructor(private readonly props: Readonly<WeatherDaysProps>) {}

    get weatherDays(): readonly Readonly<WeatherDay>[] {
        return this.props.weatherDays;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): WeatherDays {
        return WeatherDays.create({
            weatherDays: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<WeatherDaysInputProps>>,
    ): Result<WeatherDaysProps, z.ZodError<WeatherDaysProps>> {
        const result = WeatherDaysSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<WeatherDaysInputProps>,
    ): Result<WeatherDays, z.ZodError<WeatherDaysProps>> {
        return WeatherDays.validate(props).map(
            (validatedProps) => new WeatherDays(validatedProps),
        );
    }
}
